/**
 * A table header that ORDERS and NARROWS the column under it (design: "Led AI
 * Console", the ISSUES table).
 *
 * ## Why the two live in one menu
 *
 * The artboard's header opens a filter menu; the console's header already
 * sorted on tap. Both cannot own the tap, and a header with two separate
 * targets a few pixels apart is a header people mis-click.
 *
 * So the tap opens one menu that carries both: the two directions at the top,
 * the column's values underneath. That is the arrangement Linear and Notion
 * settled on for the same collision, and it makes the sort DISCOVERABLE —
 * tap-to-sort was a thing you had to already know the table did.
 *
 * ## What the chip says
 *
 * The column's name until something is chosen, then the choice itself, with
 * an inline clear. A header reading "Company" above a table showing only Apex
 * rows is the state that makes a reader think the list is broken.
 */

import { useRef, useState, type CSSProperties } from "react";
import { C, FONT_MONO } from "../tokens";
import { AnchoredPanel } from "./AnchoredPanel";
import { META } from "./chrome";
import type { ColumnSort, SortDirection } from "./columnSort";
import { worstTone, type ColumnFilterOption, type WorstTone } from "./columnFilter";

const TONE: Record<WorstTone, string> = {
  red: C.red,
  amber: C.amber,
  mid: C.propLabel,
  pale: C.calOutside,
};

export function ColumnHeader({
  column,
  label,
  sort,
  onSort,
  options,
  onFilter,
  align = "left",
  style,
}: {
  column: string;
  label: string;
  sort: ColumnSort | null;
  /** `undefined` for a column that cannot be ordered. */
  onSort?: (column: string, direction: SortDirection) => void;
  /** `undefined` for a column that cannot be narrowed. */
  options?: ColumnFilterOption[];
  onFilter?: (column: string, value: string | null) => void;
  align?: "left" | "right";
  style?: CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const sorted = sort?.column === column;
  const direction: SortDirection | undefined = sorted ? sort!.direction : undefined;
  const chosen = options?.find((option) => option.selected && option.value !== null);
  const filtered = !!chosen;

  // A column that does nothing is a plain label, not a button that looks like
  // one and answers nothing.
  if (!onSort && !options) {
    return <span style={{ ...style, display: "block", textAlign: align }}>{label}</span>;
  }

  return (
    <div
      ref={anchorRef}
      style={{
        position: "relative",
        minWidth: 0,
        display: "flex",
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        ...style,
      }}
    >
      <button
        type="button"
        className="kb colhdr"
        data-testid="column-header"
        data-column={column}
        data-active={sorted ? direction : "off"}
        data-filtered={filtered ? "true" : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-sort={sorted ? (direction === "asc" ? "ascending" : "descending") : "none"}
        onClick={() => setOpen((was) => !was)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          // 4 and not 5, and 5px of right padding and not 6: the caret costs
          // the label 21px in a 70px column, and PRIORITY was the first
          // casualty. Every pixel here is one the column name gets back.
          gap: 4,
          // The artboard's chip: 26px tall, pulled 8px left so the label still
          // lines up with the column under it.
          height: 26,
          marginLeft: align === "right" ? 0 : -8,
          marginRight: align === "right" ? -5 : 0,
          padding: "0 5px 0 8px",
          borderRadius: 7,
          border: "none",
          // Ground comes from the stylesheet (`.colhdr`), not from here: an
          // inline `background` outranks every rule, and the hover ground is
          // half of what tells a reader the header opens something.
          color: filtered || sorted ? C.propValue : "inherit",
          font: "inherit",
          letterSpacing: "inherit",
          textTransform: "inherit",
          minWidth: 0,
          cursor: "pointer",
        }}
      >
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {chosen ? chosen.label : label}
        </span>
        {sorted ? (
          <svg
            width="9"
            height="9"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden
            style={{ flex: "none", transform: direction === "desc" ? "rotate(180deg)" : undefined }}
          >
            <path
              d="M6 9.5V2.5M3 5.5L6 2.5l3 3"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          // At rest the chip was a transparent button wearing the header row's
          // own mono label — pixel-for-pixel the plain `<span>` it replaced.
          // Nobody found the filters, because nothing on the page said the
          // headers opened anything (user, 2026-09-12). This caret is the
          // whole affordance: quiet enough not to compete with six column
          // names, present enough to be seen without hovering.
          <svg
            className="colhdr-caret"
            width="8"
            height="8"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden
            data-filterable={options ? "true" : undefined}
            style={{ flex: "none" }}
          >
            <path
              d="M2.75 4.75L6 8l3.25-3.25"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
        {filtered && (
          // Clearing one column without opening its menu, as the design draws
          // it. A `<span role="button">` rather than a nested `<button>`,
          // which is invalid HTML and swallows its own click in some engines.
          <span
            role="button"
            tabIndex={0}
            aria-label={`Clear ${label.toLowerCase()} filter`}
            data-testid="column-header-clear"
            onClick={(event) => {
              event.stopPropagation();
              onFilter?.(column, null);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              event.stopPropagation();
              onFilter?.(column, null);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 14,
              height: 14,
              flex: "none",
              borderRadius: 4,
              background: C.meterOff,
              cursor: "pointer",
            }}
          >
            <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden>
              <path
                d="M3 3l6 6M9 3l-6 6"
                stroke={C.propValue}
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </span>
        )}
      </button>

      {open && (
        <AnchoredPanel
          anchorRef={anchorRef}
          onClose={() => setOpen(false)}
          role="menu"
          label={`${label} column`}
          testId={`column-menu-${column}`}
          minWidth={280}
          maxWidth={280}
          panelStyle={{
            padding: 6,
            borderRadius: 14,
            // The menu is its own surface: the header row above it is mono,
            // uppercase and tracked, and inheriting that made every option
            // read as a heading.
            textTransform: "none",
            letterSpacing: "normal",
            fontWeight: 400,
          }}
        >
          {onSort && (
            <>
              <SortRow
                label="Sort ascending"
                active={sorted && direction === "asc"}
                onClick={() => {
                  onSort(column, "asc");
                  setOpen(false);
                }}
              />
              <SortRow
                label="Sort descending"
                active={sorted && direction === "desc"}
                onClick={() => {
                  onSort(column, "desc");
                  setOpen(false);
                }}
              />
              {options && <Divider />}
            </>
          )}

          {options?.map((option, index) => (
            <div key={option.value ?? "__all__"}>
              <button
                type="button"
                className="kb colopt"
                role="menuitemradio"
                aria-checked={option.selected}
                data-testid="column-option"
                data-value={option.value ?? ""}
                onClick={() => {
                  onFilter?.(column, option.value);
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  height: 38,
                  padding: "0 10px",
                  borderRadius: 9,
                  border: "none",
                  background: option.selected ? C.railMenuOn : "transparent",
                  font: "inherit",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                {/* The oldest row behind this option. Only where the option is
                    a real value: the "all" row's dot would be the whole
                    table's worst, which is a fact about the table and not
                    about a choice. */}
                {option.value !== null && (
                  <span
                    aria-hidden
                    style={{
                      width: 8,
                      height: 8,
                      flex: "none",
                      borderRadius: 4,
                      background: TONE[worstTone(option.worstDays)],
                    }}
                  />
                )}
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 16.1,
                    fontWeight: option.selected ? 600 : 500,
                    color: C.propValue,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {option.label}
                </span>
                <span
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: 12.08,
                    color: C.propLabel,
                    whiteSpace: "nowrap",
                  }}
                >
                  {option.count}
                </span>
                {option.selected && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M5 12.5l4.5 4.5L19 7.5"
                      stroke={C.sub}
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
              {index === 0 && options.length > 1 && <Divider />}
            </div>
          ))}
        </AnchoredPanel>
      )}
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: C.railEdge, margin: "4px 6px" }} />;
}

function SortRow({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="kb colopt"
      role="menuitemradio"
      aria-checked={active}
      data-testid="column-sort"
      data-direction={label.endsWith("ascending") ? "asc" : "desc"}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        height: 34,
        padding: "0 10px",
        borderRadius: 9,
        border: "none",
        background: active ? C.railMenuOn : "transparent",
        font: "inherit",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span style={{ ...META, fontSize: 10.35, color: C.propLabel, flex: 1 }}>{label}</span>
      {active && (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M5 12.5l4.5 4.5L19 7.5"
            stroke={C.sub}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
}
